import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { customJoinPageService } from "@/services/customJoinPage.service";
import type { OAuthConsentRequest } from "@/types/customJoinPage";

const SCOPE_LABELS: Record<string, string> = {
  "join-page:read": "See your join page draft, published versions and brand details",
  "join-page:write": "Save drafts and publish your custom join page",
};

/**
 * OAuth consent screen for the MCP connector. An AI client (claude.ai,
 * Claude Code, …) sent the brand here; approving hands the client a
 * brand-scoped token that can only touch the custom join page.
 */
export default function OAuthConsent() {
  const [params] = useSearchParams();
  const requestId = params.get("request") ?? "";
  const [request, setRequest] = useState<OAuthConsentRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!requestId) {
      setError("This link is missing its request id. Start again from your AI assistant.");
      return;
    }
    customJoinPageService
      .describeOAuthRequest(requestId)
      .then(setRequest)
      .catch((e: Error) => setError(e.message));
  }, [requestId]);

  const decide = async (approve: boolean) => {
    setBusy(true);
    try {
      const { redirectTo } = approve
        ? await customJoinPageService.approveOAuthRequest(requestId)
        : await customJoinPageService.denyOAuthRequest(requestId);
      window.location.assign(redirectTo);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md py-12">
      <Card>
        <CardHeader>
          <CardTitle>Connect to Reveshare</CardTitle>
          <CardDescription>
            {request
              ? `${request.client.clientName} is asking for access to your custom join page.`
              : "Checking the request…"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : !request ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (
            <>
              <p className="mb-2 text-sm font-medium">It will be able to:</p>
              <ul className="mb-6 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {request.scopes.map((s) => (
                  <li key={s}>{SCOPE_LABELS[s] ?? s}</li>
                ))}
              </ul>
              <p className="mb-6 text-xs text-muted-foreground">
                Nothing else in your account is shared. You can disconnect it any time from
                Settings → Custom page → Connected apps.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="outline" disabled={busy} onClick={() => decide(false)}>
                  Deny
                </Button>
                <Button disabled={busy} onClick={() => decide(true)}>
                  {busy ? "Connecting…" : "Allow"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
